import { z } from "zod";
import { parse, isValid, differenceInYears } from "date-fns";
import {
  accountNumberSchema,
  ifscCodeSchema,
  accountTypeSchema,
  nameSchema,
  panSchema,
} from "../common";
import {
  BELONGS_TO,
  GENDER,
  INCOME_RANGE,
  OCCUPATION,
  PEP_STATUS,
  SOURCE_OF_WEALTH,
} from "../../constants";

export const sourceOfWealthSchema = z.enum(
  SOURCE_OF_WEALTH.map((s) => s.value) as [string, ...string[]]
);
export type SourceOfWealth = z.infer<typeof sourceOfWealthSchema>;

export const incomeSlabSchema = z.enum(
  INCOME_RANGE.map((i) => i.value) as [string, ...string[]]
);
export type IncomeSlab = z.infer<typeof incomeSlabSchema>;

export const occupationSchema = z.enum(
  OCCUPATION.map((o) => o.value) as [string, ...string[]]
);
export type Occupation = z.infer<typeof occupationSchema>;

export const genderSchema = z.enum(
  GENDER.map((g) => g.value) as [string, ...string[]],
  {
    errorMap: (issue, ctx) => {
      if (issue.code === z.ZodIssueCode.invalid_enum_value) {
        return { message: "Gender is required" };
      }
      return { message: ctx.defaultError };
    },
  }
);
export type Gender = z.infer<typeof genderSchema>;

export const pepDetailsSchema = z.enum(
  PEP_STATUS.map((p) => p.value) as [string, ...string[]]
);

export type PepStatus = z.infer<typeof pepDetailsSchema>;

export const PersonalInfoSchema = z
  .object({
    name: nameSchema,
    pan: panSchema,
    gender: genderSchema,
    date_of_birth: z
      .string()
      .min(1)
      .refine(
        (val) => {
          // Accepts date in YYYY-MM-DD or similar format
          const dob = parse(val, "yyyy-MM-dd", new Date());
          if (!isValid(dob)) return false;
          return differenceInYears(new Date(), dob) >= 18;
        },
        {
          message: "Should be >18 years old",
        }
      ),
    country_of_birth: z.string().min(1, "Country of birth is required"),
  })
  .refine((data) => data.gender !== "", {
    message: "Gender is required",
    path: ["gender"],
  });

export const AddressInfoSchema = z.object({
  line1: z
    .string()
    .max(40, "Address line 1 cannot exceed 40 characters")
    .refine((val) => val.trim() !== "", {
      message: "Address is required",
    }),
  line2: z
    .string()
    .max(40, "Address line 2 cannot exceed 40 characters")
    .optional(),
  line3: z
    .string()
    .max(40, "Address line 3 cannot exceed 40 characters")
    .optional(),
  country: z.string().refine((val) => val.trim() !== "", {
    message: "Country is required",
  }),
  city: z.string().refine((val) => val.trim() !== "", {
    message: "City is required",
  }),
  state: z.string().refine((val) => val.trim() !== "", {
    message: "State is required",
  }),
  postal_code: z
    .string()
    .refine((val) => val.trim() !== "", {
      message: "Postal code is required",
    })
    .refine((val) => /^\d{6}$/.test(val), {
      message: "Postal code must be 6 digits",
    }),
});

export const ContactInfoSchema = z.object({
  email: z.string().email(),
  number: z.string().min(1),
});

export const BankDetailsSchema = z
  .object({
    accountNumber: accountNumberSchema,
    confirmAccountNumber: accountNumberSchema,
    ifsc: ifscCodeSchema,
    type: accountTypeSchema,
    primaryAccountHolderName: z.string().optional(),
  })
  .refine(
    (data) => {
      return data.accountNumber === data.confirmAccountNumber;
    },
    {
      message: "Both account numbers must match",
      path: ["confirmAccountNumber"],
    }
  )
  .refine(
    (data) => {
      return data.type === "SB" || data.type === "CB";
    },
    {
      message: "Account type is required",
      path: ["type"],
    }
  );

const nomineeDetailsBaseSchema = z.object({
  nominate: z.boolean().optional(),
  name: z
    .string()
    .regex(/^[a-zA-Z\s]*$/, "Cannot contain numbers or special characters"),
  relationship: z.string(),
  email: z.string(),
  mobile: z.string(),
  date_of_birth: z.string().nullable().optional(),
  guardian_name: z.string().optional(),
  guardian_pan: z.string().optional(),
  identity_type: z.string(),
  identity_number: z.string(),
  address_line_1: z.string(),
  address_line_2: z.string().optional(),
  address_line_3: z.string().optional(),
  country: z.string(),
  city: z.string(),
  pin_code: z.string(),
  applicable_percentage: z.number(),
});

type NomineeDetails = z.infer<typeof nomineeDetailsBaseSchema>;

/**
 * Mandatory nominee details - name, relationship and the allocation share.
 * Everything else is optional going forward.
 */
const validateMandatoryNomineeDetails = (
  data: NomineeDetails,
  ctx: z.RefinementCtx
) => {
  if (!data.name) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Name is required",
      path: ["name"],
    });
  }

  if (!data.relationship) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Relationship is required",
      path: ["relationship"],
    });
  }

  if (data.applicable_percentage < 0 || data.applicable_percentage > 100) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Allocation percentage must be between 0 and 100",
      path: ["applicable_percentage"],
    });
  }
};

/**
 * Validation for the remaining (optional) nominee details. These are never
 * mandatory - they are only format-checked when a value has been entered.
 */
const validateOptionalNomineeDetails = (
  data: NomineeDetails,
  ctx: z.RefinementCtx
) => {
  if (data.identity_number) {
    if (data.identity_type === "1") {
      if (!panSchema.safeParse(data.identity_number).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid PAN format",
          path: ["identity_number"],
        });
      }
    }

    if (data.identity_type === "2") {
      if (!/^\d{4}$/.test(data.identity_number)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Aadhaar number must contain only the last 4 digits",
          path: ["identity_number"],
        });
      }
    }

    if (!data.identity_type) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Identity type is required",
        path: ["identity_type"],
      });
    }
  } else if (data.identity_type) {
    // Identity is optional as a whole, but a type without a number is not.
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Identity number is required",
      path: ["identity_number"],
    });
  }

  // A minor nominee still needs a guardian.
  if (data.date_of_birth) {
    const dob = parse(data.date_of_birth, "yyyy-MM-dd", new Date());
    if (differenceInYears(new Date(), dob) < 18) {
      if (!data.guardian_name) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Guardian name is required",
          path: ["guardian_name"],
        });
      }
      if (!data.guardian_pan) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Guardian PAN is required",
          path: ["guardian_pan"],
        });
      }
    }
  }

  if (
    data.mobile &&
    !z
      .string()
      .regex(/^\d{10}$/)
      .safeParse(data.mobile).success
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Phone number must be 10 digits",
      path: ["mobile"],
    });
  }

  if (data.email && !z.string().email().safeParse(data.email).success) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Invalid email format",
      path: ["email"],
    });
  }

  // Address line validation
  if (data.address_line_1 && data.address_line_1.length > 40) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Address line 1 cannot exceed 40 characters",
      path: ["address_line_1"],
    });
  }

  if (data.address_line_2 && data.address_line_2.length > 40) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Address line 2 cannot exceed 40 characters",
      path: ["address_line_2"],
    });
  }

  if (data.address_line_3 && data.address_line_3.length > 40) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Address line 3 cannot exceed 40 characters",
      path: ["address_line_3"],
    });
  }

  if (data.pin_code && !/^\d{6}$/.test(data.pin_code)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Postal code must be 6 digits",
      path: ["pin_code"],
    });
  }
};

/**
 * Nominee validation. Only name, relationship and the allocation share are
 * mandatory - every other field is optional and is validated only when it has
 * been filled in.
 */
export const NomineeDetailsSchema = nomineeDetailsBaseSchema.superRefine(
  (data, ctx) => {
    if (!data.nominate) {
      return;
    }
    validateMandatoryNomineeDetails(data, ctx);
    validateOptionalNomineeDetails(data, ctx);
  }
);

export const OtherInfoSchema = z.object({
  occupation: occupationSchema,
  income_slab: incomeSlabSchema,
  source_of_wealth: sourceOfWealthSchema,
  pep_details: pepDetailsSchema,
});

export const belongsToEnum = z.enum(
  BELONGS_TO.map((b) => b.value) as [string, ...string[]]
);

export type BelongsTo = z.infer<typeof belongsToEnum>;

export const ContactDetailsSchema = z
  .object({
    number: z.string().min(1),
    email: z.string().min(1, "Email is required").email("Invalid email format"),
    phone_belongs_to: belongsToEnum,
    email_belongs_to: belongsToEnum,
    isd: z.string(),
  })
  .refine(
    (data) => {
      return data.email !== "";
    },
    {
      message: "Email is required",
    }
  );
